import React from "react";
import { TopNav } from "@/components/TopNav";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { useLang } from "@/context/LanguageContext";
import { pageTr } from "@/i18n/pageTranslations";
import { Info, Mountain, Thermometer, Waves, ShieldCheck, Diamond } from "lucide-react";

const GEMS = [
  {
    id: "ruby", nameFr: "RUBIS", nameEn: "RUBY", color: "#E86B8C", accent: "rgba(224,17,95,0.22)", img: "/gems/ruby.png",
    formula: "Al₂O₃", formulaFr: "Oxyde d'aluminium", formulaEn: "Aluminum oxide",
    crystal: "Trigonal (Rhomboédrique)",
    steps: {
      fr: [
        "Le rubis est une variété rouge de corindon. Sa couleur rouge est due à la présence de chrome (Cr³⁺).",
        "Formé principalement dans les roches métamorphiques telles que les marbres, ou dans les roches mafiques et ultramafiques.",
        "Le corindon cristallise sous haute température et pression lors du métamorphisme, avec des fluides riches en éléments comme le chrome.",
        "L'érosion libère les cristaux qui sont transportés par l'eau et se concentrent dans les alluvions (rivières, graviers).",
      ],
      en: [
        "Ruby is the red variety of corundum. Its red color is due to the presence of chromium (Cr³⁺).",
        "Mainly formed in metamorphic rocks such as marbles, or in mafic and ultramafic rocks.",
        "Corundum crystallizes under high temperature and pressure during metamorphism, with fluids rich in elements such as chromium.",
        "Erosion releases crystals that are transported by water and concentrated in alluvial deposits (rivers, gravels).",
      ],
    },
    keyEl: "Chromium (Cr³⁺)", colorLabel: { fr: "Rouge à rouge vif", en: "Red to vivid red" },
  },
  {
    id: "sapphire", nameFr: "SAPHIR", nameEn: "SAPPHIRE", color: "#6B9EE8", accent: "rgba(15,82,186,0.22)", img: "/gems/sapphire.png",
    formula: "Al₂O₃", formulaFr: "Oxyde d'aluminium", formulaEn: "Aluminum oxide",
    crystal: "Trigonal (Rhomboédrique)",
    steps: {
      fr: [
        "Le saphir est une variété de corindon de toutes les couleurs sauf le rouge (rubis). La couleur bleue provient du fer (Fe) et du titane (Ti).",
        "Formé dans les roches métamorphiques (gneiss, schistes) et dans les roches mafiques (basaltes).",
        "Cristallisation lente à haute température et pression, avec enrichissement en fer et titane dans les fluides géologiques.",
        "Les cristaux sont libérés par l'érosion, transportés par les rivières et concentrés dans les alluvions.",
      ],
      en: [
        "Sapphire is a variety of corundum in all colors except red (ruby). Blue color comes from iron (Fe) and titanium (Ti).",
        "Formed in metamorphic rocks (gneiss, schists) and in mafic rocks (basalts).",
        "Slow crystallization at high temperature and pressure, with enrichment in iron and titanium from geological fluids.",
        "Crystals are released by erosion, transported by rivers and concentrated in alluvial deposits.",
      ],
    },
    keyEl: "Fer (Fe²⁺) – Titane (Ti³⁺)", colorLabel: { fr: "Bleu (et autres couleurs)", en: "Blue (and other colors)" },
  },
  {
    id: "emerald", nameFr: "ÉMERAUDE", nameEn: "EMERALD", color: "#6BE895", accent: "rgba(80,200,120,0.22)", img: "/gems/emerald.png",
    formula: "Be₃Al₂Si₆O₁₈", formulaFr: "Silicate de béryllium et d'aluminium", formulaEn: "Beryllium aluminum silicate",
    crystal: "Hexagonal",
    steps: {
      fr: [
        "L'émeraude est une variété verte de béryl. Sa couleur verte est due au chrome (Cr) et/ou vanadium (V).",
        "Formée dans les roches métamorphiques (schistes noirs, phyllites) ou dans les pegmatites granitiques.",
        "Les fluides hydrothermaux riches en béryllium, chrome et vanadium circulent dans les fractures et cavités, où ils cristallisent en émeraude.",
        "L'érosion libère les cristaux qui peuvent être transportés et concentrés dans les sédiments alluvionnaires.",
      ],
      en: [
        "Emerald is the green variety of beryl. Its green color is due to chromium (Cr) and/or vanadium (V).",
        "Formed in metamorphic rocks (black shales, phyllites) or in granitic pegmatites.",
        "Hydrothermal fluids rich in beryllium, chromium and vanadium circulate in fractures and cavities, where emerald crystallizes.",
        "Erosion releases crystals that may be transported and concentrated in alluvial sediments.",
      ],
    },
    keyEl: "Chrome (Cr³⁺) – Vanadium (V³⁺)", colorLabel: { fr: "Vert à vert intense", en: "Green to vivid green" },
  },
];

const STEP_ICONS = [Info, Mountain, Thermometer, Waves];

const FormationPage = () => {
  const { lang } = useLang();
  const t = pageTr[lang].formation;
  return (
    <div className="min-h-screen bg-[#050811] text-[#F8F5EE] flex flex-col">
      <SEO
        title={t.title}
        path="/formation"
        noindex
        description={lang === "fr"
          ? "GEM PRICE — Formation naturelle des pierres précieuses expliquée : composition, cristallisation, gisements. Complément aux fiches tarifaires par pays et par carat."
          : "GEM PRICE — Natural formation of precious gemstones explained: composition, crystallisation, deposits. Companion to certified pricing sheets by origin and per carat."}
      />
      <TopNav />
      <div className="max-w-7xl mx-auto px-6 md:px-14 py-10 space-y-6">
        <header className="text-center">
          <h1 className="font-serif text-3xl md:text-5xl font-light tracking-wide text-[#E2CFA4]">{lang === "fr" ? "FORMATION NATURELLE DES PIERRES PRÉCIEUSES" : "NATURAL FORMATION OF PRECIOUS GEMSTONES"}</h1>
          <p className="mt-2 text-[13px] text-[#A1A7B3] italic">{t.tagline}</p>
        </header>

        <div className="grid lg:grid-cols-3 gap-6">
          {GEMS.map((g) => (
            <article key={g.id} data-testid={`formation-${g.id}`} className="rounded-sm border border-[#C1A661]/15 bg-[#080C17]"
              style={{ boxShadow: `inset 0 0 80px ${g.accent}` }}>
              <header className="p-5 border-b border-[#C1A661]/10 text-center">
                <h2 className="font-serif text-2xl tracking-wider" style={{ color: g.color }}>{lang === "fr" ? g.nameFr : g.nameEn}</h2>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A7B3] mt-0.5">{lang === "fr" ? g.nameEn : g.nameFr}</div>
              </header>
              <div className="p-5 flex flex-col items-center">
                <img src={g.img} alt={g.nameEn} className="w-32 h-32 object-cover rounded-sm" />
                <div className="mt-4 text-center">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#C1A661]">{t.chem}</div>
                  <div className="font-serif text-3xl text-[#E2CFA4] mt-1">{g.formula}</div>
                  <div className="text-[11px] text-[#A1A7B3] font-light">{lang === "fr" ? g.formulaFr : g.formulaEn}</div>
                </div>
                <div className="mt-3 flex items-center gap-3 text-[11px] text-[#D8DBE2]">
                  <Diamond size={14} style={{ color: g.color }} />
                  <div>
                    <div className="text-[9px] tracking-[0.2em] uppercase text-[#A1A7B3]">{t.crystal}</div>
                    <div>{g.crystal}</div>
                  </div>
                </div>
              </div>
              <div className="px-5 py-4 border-t border-[#C1A661]/10">
                <div className="text-center text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: g.color }}>{t.section}</div>
                <div className="space-y-3">
                  {g.steps[lang].map((s, i) => {
                    const Icon = STEP_ICONS[i];
                    return (
                      <div key={i} className="flex gap-3">
                        <div className="w-8 h-8 shrink-0 rounded-full border flex items-center justify-center" style={{ borderColor: g.color, color: g.color }}>
                          <Icon size={14} strokeWidth={1.4} />
                        </div>
                        <div>
                          <div className="text-[10px] tracking-[0.2em] uppercase font-medium" style={{ color: g.color }}>{i + 1}. {t.steps[i]}</div>
                          <p className="text-[12px] text-[#D8DBE2] font-light mt-0.5 leading-relaxed">{s}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="p-4 border-t border-[#C1A661]/10 flex items-center justify-between text-[11px]">
                <div>
                  <div className="text-[9px] tracking-[0.2em] uppercase" style={{ color: g.color }}>{t.keyElement}</div>
                  <div className="text-[#E2CFA4]">{g.keyEl}</div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] tracking-[0.2em] uppercase" style={{ color: g.color }}>{t.color}</div>
                  <div className="text-[#E2CFA4]">{g.colorLabel[lang]}</div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-4 text-[11px] text-[#A1A7B3] font-light">
          <div className="flex gap-2 items-start"><Info size={14} className="text-[#C1A661] mt-0.5 shrink-0" /><span>{t.footer1}</span></div>
          <div className="flex gap-2 items-start"><ShieldCheck size={14} className="text-[#C1A661] mt-0.5 shrink-0" /><span>{t.footer2}</span></div>
          <div className="flex gap-2 items-start"><Diamond size={14} className="text-[#C1A661] mt-0.5 shrink-0" /><span>{t.footer3}</span></div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default FormationPage;
